import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaTimes,
  FaCommentDots,
  FaUser,
  FaTag,
  FaCheck,
} from "react-icons/fa";
import ReactQuill, { Quill } from "react-quill";
import ImageResize from "quill-image-resize-module-react";
import "react-quill/dist/quill.snow.css";
import CommonModal from "./CommonModal";
import { catchError, commonModalSetting } from "JS/common";
import { postAdminReview } from "JS/API";
import { designTokens, commonStyles } from "utils/designSystem";
import { detailModalStyles } from "utils/modalDesignSystem";
import "utils/quillImageFormat"; // 이미지 width/height/style 보존 (재편집 시 크기 유지)

Quill.register("modules/imageResize", ImageResize);

const REVIEW_TYPES = ["출원후기", "등록가능성검토 후기"];

// 현재 로컬 시각 → datetime-local 입력 형식("YYYY-MM-DDTHH:mm")
const nowLocalDateTime = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
};

const INITIAL_FORM = {
  review_type: "출원후기",
  title: "",
  context: "",
  author_name: "",
  is_member: true,
  created_at: "", // datetime-local (열릴 때 현재 시각으로 자동 채움)
};

const CsReviewCreateModal = ({ setModal, onSuccess }) => {
  const [form, setForm] = useState(() => ({
    ...INITIAL_FORM,
    created_at: nowLocalDateTime(),
  }));
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });
  const navigate = useNavigate();
  const quillRef = useRef(null);

  const update = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // 이미지 리사이즈는 onChange를 발생시키지 않으므로
  // 제출 시 에디터 DOM에서 최신 HTML을 직접 읽는다.
  const getEditorHtml = () =>
    quillRef.current ? quillRef.current.getEditor().root.innerHTML : form.context;

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      ["image"],
      [{ align: [] }, { color: [] }, { background: [] }],
      ["clean"],
    ],
    imageResize: {
      parchment: Quill.import("parchment"),
      modules: ["Resize", "DisplaySize"],
    },
  };
  const quillFormats = [
    "header", "bold", "italic", "underline", "strike", "blockquote",
    "link", "image", "align", "color", "background",
    "width", "height", "style", "alt", // 이미지 리사이즈 속성 보존
  ];

  const styles = {
    overlay: detailModalStyles.overlay,
    container: detailModalStyles.container,
    header: {
      padding: designTokens.spacing.xxl,
      borderBottom: `1px solid ${designTokens.colors.border}`,
      display: "flex", alignItems: "center", justifyContent: "space-between",
    },
    title: detailModalStyles.title,
    closeButton: {
      ...commonStyles.buttonBase,
      padding: designTokens.spacing.md, borderRadius: "50%",
      backgroundColor: "transparent", color: designTokens.colors.text.tertiary,
      border: "none", width: "36px", height: "36px",
      display: "flex", alignItems: "center", justifyContent: "center",
      cursor: "pointer",
    },
    content: detailModalStyles.content,
    section: detailModalStyles.section,
    sectionTitle: {
      display: "flex", alignItems: "center", gap: designTokens.spacing.md,
      fontSize: "15px", fontWeight: "600",
      color: designTokens.colors.text.primary,
      marginBottom: designTokens.spacing.lg,
      paddingBottom: designTokens.spacing.md,
      borderBottom: `2px solid ${designTokens.colors.primary}`,
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
      gap: designTokens.spacing.lg,
    },
    field: {
      display: "flex", flexDirection: "column", gap: designTokens.spacing.sm,
    },
    label: {
      fontSize: "13px", fontWeight: "600",
      color: designTokens.colors.text.secondary,
      display: "flex", alignItems: "center", gap: designTokens.spacing.sm,
    },
    required: { color: "#dc2626", marginLeft: "2px" },
    input: {
      padding: `${designTokens.spacing.md} ${designTokens.spacing.md}`,
      border: `1px solid ${designTokens.colors.border}`,
      borderRadius: designTokens.borderRadius.md,
      fontSize: "14px", fontFamily: "inherit",
      backgroundColor: designTokens.colors.surface,
    },
    radioGroup: {
      display: "flex", gap: designTokens.spacing.md, flexWrap: "wrap",
    },
    radioOption: {
      display: "flex", alignItems: "center", gap: designTokens.spacing.sm,
      padding: `${designTokens.spacing.sm} ${designTokens.spacing.lg}`,
      borderRadius: designTokens.borderRadius.md,
      border: `1px solid ${designTokens.colors.borderLight}`,
      backgroundColor: designTokens.colors.surface,
      fontSize: "13px", cursor: "pointer", userSelect: "none",
    },
    radioOptionSelected: {
      backgroundColor: designTokens.colors.primary,
      color: designTokens.colors.surface,
      borderColor: designTokens.colors.primary,
    },
    footer: {
      padding: designTokens.spacing.xl,
      borderTop: `1px solid ${designTokens.colors.border}`,
      display: "flex", justifyContent: "flex-end", gap: designTokens.spacing.md,
      backgroundColor: designTokens.colors.background,
    },
    primaryButton: {
      ...commonStyles.buttonBase,
      backgroundColor: designTokens.colors.primary,
      color: designTokens.colors.surface,
      padding: `${designTokens.spacing.md} ${designTokens.spacing.xxl}`,
      fontSize: "14px", fontWeight: "600",
      cursor: submitting ? "not-allowed" : "pointer",
      opacity: submitting ? 0.6 : 1,
    },
    secondaryButton: detailModalStyles.secondaryButton,
  };

  const radio = (value, checked, onClick, icon) => (
    <div
      style={{
        ...styles.radioOption,
        ...(checked ? styles.radioOptionSelected : {}),
      }}
      onClick={onClick}
    >
      {icon}
      <span>{value}</span>
    </div>
  );

  const validate = (html) => {
    if (!form.author_name.trim()) return "작성자 이름을 입력해 주세요.";
    if (form.author_name.length > 20)
      return "작성자 이름은 20자 이내로 입력해 주세요.";
    if (!form.review_type) return "후기 유형을 선택해 주세요.";
    if (!form.title.trim()) return "제목을 입력해 주세요.";
    if (form.title.length > 300) return "제목은 300자 이내로 입력해 주세요.";
    // Quill 빈 입력은 "<p><br></p>" 형태이므로 텍스트/이미지 기준으로 검사
    const plain = html.replace(/<[^>]*>/g, "").trim();
    const hasImage = /<img/i.test(html);
    if (!plain && !hasImage) return "내용을 입력해 주세요.";
    return null;
  };

  // datetime-local("2026-06-01T10:00") → "2026-06-01 10:00:00"
  const toServerDate = (v) => {
    if (!v) return "";
    const [d, t] = v.split("T");
    if (!t) return d;
    const sec = t.length === 5 ? `${t}:00` : t;
    return `${d} ${sec}`;
  };

  const submit = async () => {
    // 리사이즈된 이미지 크기까지 반영되도록 에디터 DOM에서 직접 읽는다.
    const editorHtml = getEditorHtml();
    const err = validate(editorHtml);
    if (err) {
      return commonModalSetting(setAlertBox, true, "alert", err);
    }
    setSubmitting(true);

    const body = {
      review_type: form.review_type,
      title: form.title.trim(),
      context: editorHtml,
      author_name: form.author_name.trim(),
      is_member: form.is_member,
    };
    const serverDate = toServerDate(form.created_at);
    if (serverDate) body.created_at = serverDate;

    const result = await postAdminReview(body);
    setSubmitting(false);
    if (typeof result === "object") {
      setAlert("createComplete");
      commonModalSetting(
        setAlertBox, true, "alert",
        "후기가 정상적으로 등록되었습니다."
      );
    } else {
      return await catchError(result, navigate, setAlertBox, setAlert);
    }
  };

  return (
    <>
      <div style={styles.overlay}>
        <div style={styles.container} onClick={(e) => e.stopPropagation()}>
          <div style={styles.header}>
            <h2 style={styles.title}>
              <FaCommentDots />
              운영용 후기 작성
            </h2>
            <button style={styles.closeButton} onClick={() => setModal(false)}>
              <FaTimes size={16} />
            </button>
          </div>

          <div style={styles.content}>
            {/* 표시 방식 */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>
                <FaUser />
                표시 방식
              </h3>
              <div style={styles.radioGroup}>
                {radio(
                  "회원 글로 표시",
                  form.is_member === true,
                  () => update("is_member", true),
                  <FaUser size={12} />
                )}
                {radio(
                  "비회원 글로 표시",
                  form.is_member === false,
                  () => update("is_member", false),
                  <FaUser size={12} />
                )}
              </div>
            </div>

            {/* 기본 정보 */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>
                <FaTag />
                기본 정보
              </h3>
              <div style={styles.grid}>
                <div style={styles.field}>
                  <label style={styles.label}>
                    작성자 이름<span style={styles.required}>*</span>
                  </label>
                  <input
                    style={styles.input}
                    type="text"
                    maxLength={20}
                    value={form.author_name}
                    onChange={(e) => update("author_name", e.target.value)}
                    placeholder="목록에 표시될 이름"
                  />
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>
                    후기 유형<span style={styles.required}>*</span>
                  </label>
                  <select
                    style={styles.input}
                    value={form.review_type}
                    onChange={(e) => update("review_type", e.target.value)}
                  >
                    {REVIEW_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>작성일 (선택)</label>
                  <input
                    style={styles.input}
                    type="datetime-local"
                    value={form.created_at}
                    onChange={(e) => update("created_at", e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* 제목 / 내용 */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>
                <FaCommentDots />
                후기 내용
              </h3>
              <div style={styles.field}>
                <label style={styles.label}>
                  제목<span style={styles.required}>*</span>
                </label>
                <input
                  style={styles.input}
                  type="text"
                  maxLength={300}
                  value={form.title}
                  onChange={(e) => update("title", e.target.value)}
                  placeholder="제목을 입력해 주세요"
                />
              </div>
              <div
                style={{
                  ...styles.field,
                  marginTop: designTokens.spacing.lg,
                }}
              >
                <label style={styles.label}>
                  내용<span style={styles.required}>*</span>
                </label>
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={form.context}
                  onChange={(v) => update("context", v)}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="후기 내용을 입력해 주세요"
                />
              </div>
            </div>
          </div>

          <div style={styles.footer}>
            <button
              style={styles.secondaryButton}
              onClick={() => setModal(false)}
              disabled={submitting}
            >
              취소
            </button>
            <button
              style={styles.primaryButton}
              onClick={submit}
              disabled={submitting}
            >
              <FaCheck size={12} />
              {submitting ? "등록 중..." : "등록"}
            </button>
          </div>
        </div>
      </div>

      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {
            if (alert === "createComplete") {
              if (onSuccess) onSuccess();
              setModal(false);
            } else if (alert === "logout") navigate("/");
          }}
        />
      )}
    </>
  );
};

export default CsReviewCreateModal;
